import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Tooltip,
  Paper,
  InputAdornment,
  Fade
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowUp as PrevIcon,
  KeyboardArrowDown as NextIcon
} from '@mui/icons-material';

/**
 * Componente de busca dentro do conteúdo do modal
 * Similar ao Ctrl+F do navegador
 */
const SearchInContent = ({
  isOpen,
  onClose,
  searchTerm,
  onSearchChange,
  totalMatches = 0,
  currentMatch = 0,
  onPrevMatch,
  onNextMatch,
  containerRef
}) => {
  const inputRef = useRef(null);

  // Focar no input quando abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handler para teclas especiais
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter') {
      if (e.shiftKey) {
        onPrevMatch?.();
      } else {
        onNextMatch?.();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Fade in={isOpen}>
      <Paper
        elevation={4}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          mb: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <TextField
          inputRef={inputRef}
          size="small"
          placeholder="Pesquisar no conteúdo..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        {/* Contador de resultados */}
        {searchTerm && (
          <Typography
            variant="body2"
            color={totalMatches > 0 ? 'text.secondary' : 'error'}
            sx={{ minWidth: 70, textAlign: 'center', whiteSpace: 'nowrap' }}
          >
            {totalMatches > 0 ? `${currentMatch} de ${totalMatches}` : 'Nenhum'}
          </Typography>
        )}

        {/* Navegação */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Anterior (Shift+Enter)">
            <span>
              <IconButton
                size="small"
                onClick={onPrevMatch}
                disabled={!searchTerm || totalMatches === 0}
              >
                <PrevIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Próximo (Enter)">
            <span>
              <IconButton
                size="small"
                onClick={onNextMatch}
                disabled={!searchTerm || totalMatches === 0}
              >
                <NextIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Fechar */}
        <Tooltip title="Fechar (Esc)">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </Fade>
  );
};

SearchInContent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  totalMatches: PropTypes.number,
  currentMatch: PropTypes.number,
  onPrevMatch: PropTypes.func,
  onNextMatch: PropTypes.func,
  containerRef: PropTypes.object
};

export default SearchInContent;
